import LegalLayout from "@/components/site/LegalLayout";

const Section = ({ title, children, n }) => (
  <section className="mt-12 first:mt-0">
    <div className="flex items-baseline gap-4">
      <span className="font-mono text-xs text-[#111111]/40 w-8">{n}</span>
      <h2 className="font-sora text-[22px] md:text-[26px] font-semibold tracking-tight text-[#111111]">
        {title}
      </h2>
    </div>
    <div className="mt-4 pl-12 space-y-4 font-manrope text-[16px] leading-[1.75] text-[#111111]/75">
      {children}
    </div>
  </section>
);

const Terms = () => {
  return (
    <LegalLayout
      kicker="Terms of Use"
      title="Terms of Use."
      updated="February 2026"
      path="/terms"
    >
      <p
        data-testid="terms-intro"
        className="font-manrope text-[18px] md:text-[19px] leading-[1.75] text-[#111111]/80 max-w-2xl"
      >
        These Terms of Use (&ldquo;Terms&rdquo;) form a binding agreement
        between you and <strong>Vaymilo</strong> (&ldquo;Vaymilo&rdquo;,
        &ldquo;we&rdquo;, &ldquo;us&rdquo;), an independent digital product
        studio organised under the laws of the State of Delaware, United
        States. By accessing{" "}
        <a href="https://vaymilo.com" className="text-[#2563EB] hover:underline">
          vaymilo.com
        </a>{" "}
        or any Vaymilo digital product, you agree to these Terms.
      </p>

      <Section n="01" title="Definitions">
        <p>
          &ldquo;Site&rdquo; means vaymilo.com and any related sub-domain
          operated by Vaymilo.
        </p>
        <p>
          &ldquo;Product&rdquo; means any digital kit, template, prompt pack,
          workflow, guide, or other downloadable item offered by Vaymilo.
        </p>
        <p>
          &ldquo;You&rdquo; means the person or legal entity accessing the
          Site or purchasing a Product.
        </p>
      </Section>

      <Section n="02" title="Eligibility">
        <p>
          You must be at least 18 years of age and legally capable of entering
          into a binding contract to use the Site or purchase a Product. By
          using the Site, you represent that you meet these requirements.
        </p>
      </Section>

      <Section n="03" title="Account and security">
        <p>
          Some Products may require an account at the point of purchase or
          download. You are responsible for maintaining the confidentiality of
          your account credentials and for all activity that occurs under your
          account. Notify us immediately at{" "}
          <a
            href="mailto:hello@vaymilo.com"
            className="text-[#2563EB] hover:underline"
          >
            hello@vaymilo.com
          </a>{" "}
          if you suspect unauthorised access.
        </p>
      </Section>

      <Section n="04" title="Acceptable use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Use the Site for any unlawful, harmful, or fraudulent purpose.</li>
          <li>
            Attempt to disrupt or impair the Site (DDoS, scraping at scale,
            reverse engineering, exploiting vulnerabilities).
          </li>
          <li>Abuse contact, signup, or checkout forms with spam or automation.</li>
          <li>Impersonate any person or entity, or misstate your affiliation.</li>
          <li>
            Use any Vaymilo content to train, fine-tune, or commercially benefit
            third-party AI/ML models without our prior written consent.
          </li>
        </ul>
      </Section>

      <Section n="05" title="Digital products and license">
        <p>
          When you purchase a Vaymilo Product, we grant you a{" "}
          <strong>
            limited, worldwide, non-exclusive, non-transferable, single-user
            license
          </strong>{" "}
          to use that Product for your own personal or professional work,
          including in deliverables for your clients.
        </p>
        <p>You may NOT:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Resell, sublicense, redistribute, or republish the raw source files.</li>
          <li>
            Share your purchase or account access with people outside your
            household, team, or business unless you purchased a multi-seat or
            team license.
          </li>
          <li>
            Use Vaymilo Products to create directly competing prompt packs,
            template marketplaces, or workflow stores.
          </li>
          <li>Remove copyright, attribution, or license notices.</li>
        </ul>
      </Section>

      <Section n="06" title="Pricing, payment, and taxes">
        <p>
          Prices are displayed in U.S. dollars (USD) at vaymilo.com and may
          change without notice. Any applicable sales tax, VAT, or other
          transaction taxes will be added at checkout where required by law.
        </p>
        <p>
          Payments are processed by our payment provider. We do not store
          credit-card or other payment instrument details on our servers.
        </p>
      </Section>

      <Section n="07" title="Refunds and cancellations">
        <p>
          Because our Products are digital, delivered electronically, and
          consumable immediately upon download, <strong>all sales are final
          and refunds are not available</strong>, except in the following
          cases:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>The Product was never delivered to you due to a verifiable technical fault on our side.</li>
          <li>The Product is materially different from what was described on the product page.</li>
          <li>You were charged twice for the same Product.</li>
        </ul>
        <p>
          Eligible refund requests must be submitted within 7 days of purchase
          to{" "}
          <a
            href="mailto:hello@vaymilo.com"
            className="text-[#2563EB] hover:underline"
          >
            hello@vaymilo.com
          </a>
          .
        </p>
      </Section>

      <Section n="08" title="Intellectual property">
        <p>
          All Site content — including copy, layout, branding, source code,
          design system, Products, and underlying methodology — is owned by
          Vaymilo or its licensors and is protected by U.S. and international
          intellectual property laws. The license granted to you under Section
          05 does not transfer any ownership rights.
        </p>
      </Section>

      <Section n="09" title="Third-party services">
        <p>
          The Site may link to third-party websites and services (for example,
          our store, payment processor, or email provider). Vaymilo is not
          responsible for the content, terms, or privacy practices of those
          third parties.
        </p>
      </Section>

      <Section n="10" title="Disclaimers">
        <p>
          Vaymilo Products are practical tools intended to help you write,
          plan, organise, and create. We do not warrant or guarantee any
          specific outcome — including income, ranking, growth, or
          performance — resulting from use of our Products.
        </p>
        <p>
          To the maximum extent permitted by law, the Site and Products are
          provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;, without
          warranties of any kind, express or implied.
        </p>
      </Section>

      <Section n="11" title="Limitation of liability">
        <p>
          To the maximum extent permitted by law, Vaymilo and its officers,
          employees, contractors, and affiliates shall not be liable for any
          indirect, incidental, consequential, special, or punitive damages
          arising out of or in connection with your use of the Site or any
          Product.
        </p>
        <p>
          Our total aggregate liability to you for all claims arising under
          these Terms is limited to the total amount you paid us for the
          Product(s) giving rise to the claim in the 12 months preceding the
          claim.
        </p>
      </Section>

      <Section n="12" title="Indemnification">
        <p>
          You agree to indemnify and hold Vaymilo harmless from any third-party
          claim arising from your breach of these Terms, your misuse of the
          Site or Products, or your violation of any law or third-party right.
        </p>
      </Section>

      <Section n="13" title="Termination">
        <p>
          We may suspend or terminate your access to the Site or any Product at
          any time, with or without notice, if you breach these Terms or if
          required by law. Sections related to intellectual property,
          disclaimers, limitation of liability, and governing law survive
          termination.
        </p>
      </Section>

      <Section n="14" title="Governing law and jurisdiction">
        <p>
          These Terms are governed by and construed in accordance with the laws
          of the <strong>State of Delaware, United States</strong>, without
          regard to its conflict-of-law provisions. The state and federal
          courts located in Delaware shall have exclusive jurisdiction over any
          dispute arising out of or relating to these Terms.
        </p>
      </Section>

      <Section n="15" title="Dispute resolution">
        <p>
          Before initiating any legal proceeding, you agree to first contact us
          at{" "}
          <a
            href="mailto:hello@vaymilo.com"
            className="text-[#2563EB] hover:underline"
          >
            hello@vaymilo.com
          </a>{" "}
          and make a good-faith effort to resolve the dispute informally for at
          least 30 days.
        </p>
      </Section>

      <Section n="16" title="Changes to these Terms">
        <p>
          We may update these Terms occasionally. Material changes will be
          posted here with an updated &ldquo;Last updated&rdquo; date.
          Continued use of the Site or Products after the effective date of the
          revised Terms constitutes your acceptance of the changes.
        </p>
      </Section>

      <Section n="17" title="Contact">
        <p>
          Questions about these Terms? Email{" "}
          <a
            href="mailto:hello@vaymilo.com"
            className="text-[#2563EB] hover:underline"
          >
            hello@vaymilo.com
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
};

export default Terms;
